/**
 * Service Worker Registration and Management
 */

const SW_URL = '/sw.js';

// Check if service workers are supported
export const isServiceWorkerSupported = () => {
  return 'serviceWorker' in navigator;
};

// Register the service worker
export const registerServiceWorker = async () => {
  if (!isServiceWorkerSupported()) {
    console.log('[SW] Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register(SW_URL, {
      scope: '/'
    });

    console.log('[SW] Service Worker registered:', registration.scope);

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      console.log('[SW] Update found, new worker installing');

      newWorker?.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New content available
          console.log('[SW] New content available, please refresh');
          // You can dispatch an event here to show update notification
          window.dispatchEvent(new CustomEvent('sw-update-available'));
        }
      });
    });

    // Check for updates periodically (every hour)
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000);

    return registration;
  } catch (error) {
    console.error('[SW] Registration failed:', error);
    return null;
  }
};

// Unregister service worker
export const unregisterServiceWorker = async () => {
  if (!isServiceWorkerSupported()) return false;

  const registration = await navigator.serviceWorker.ready;
  const success = await registration.unregister();
  console.log('[SW] Unregistered:', success);
  return success;
};

// Check if app is ready for offline use
export const isOfflineReady = async () => {
  if (!isServiceWorkerSupported()) return false;

  const registration = await navigator.serviceWorker.getRegistration();
  return registration?.active !== null;
};

// Request background sync (for offline form submissions)
export const requestBackgroundSync = async (tag) => {
  if (!isServiceWorkerSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    if ('sync' in registration) {
      await registration.sync.register(tag);
      console.log('[SW] Background sync registered:', tag);
      return true;
    }
  } catch (error) {
    console.error('[SW] Background sync failed:', error);
  }
  return false;
};

// Cache data for offline use
export const cacheForOffline = async (url, data) => {
  if ('caches' in window) {
    const cache = await caches.open('facilitypro-api-v1');
    const response = new Response(JSON.stringify(data), {
      headers: { 'Content-Type': 'application/json' }
    });
    await cache.put(url, response);
    console.log('[SW] Cached data for:', url);
  }
};

// Clear all caches
export const clearAllCaches = async () => {
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames
        .filter(name => name.startsWith('facilitypro-'))
        .map(name => caches.delete(name))
    );
    console.log('[SW] All caches cleared');
  }
};

// Get cached data
export const getCachedData = async (url) => {
  if ('caches' in window) {
    const cache = await caches.open('facilitypro-api-v1');
    const response = await cache.match(url);
    if (response) {
      return await response.json();
    }
  }
  return null;
};

// Check online status
export const isOnline = () => navigator.onLine;

// Listen for online/offline events
export const setupNetworkListeners = (onOnline, onOffline) => {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  // Return cleanup function
  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
  };
};

// IndexedDB helpers for offline data storage
const DB_NAME = 'FacilityProOffline';
const DB_VERSION = 1;

const openDatabase = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create stores for offline data
      if (!db.objectStoreNames.contains('pendingWorkOrders')) {
        db.createObjectStore('pendingWorkOrders', { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains('pendingInspections')) {
        db.createObjectStore('pendingInspections', { keyPath: 'id', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains('offlineData')) {
        db.createObjectStore('offlineData', { keyPath: 'key' });
      }
    };
  });
};

// Save data for offline sync
export const saveForOfflineSync = async (storeName, data) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.add({ data, timestamp: Date.now() });

    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      resolve(request.result);
      // Request background sync
      requestBackgroundSync(`sync-${storeName}`);
    };
  });
};

// Get pending offline items
export const getPendingOfflineItems = async (storeName) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
};

// Remove synced item
export const removeSyncedItem = async (storeName, id) => {
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
};

export default {
  register: registerServiceWorker,
  unregister: unregisterServiceWorker,
  isSupported: isServiceWorkerSupported,
  isOfflineReady,
  isOnline,
  setupNetworkListeners,
  cacheForOffline,
  getCachedData,
  clearAllCaches,
  saveForOfflineSync,
  getPendingOfflineItems,
  removeSyncedItem,
};
